import React, { useCallback, useEffect } from 'react';
import { lowerCase } from 'lodash';
import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';

import { useBrandVoicesActions, useCustomModels, usePerformanceChannelData } from 'src/store';
import { useTrackingEvent } from 'src/services';
import * as events from 'src/lib/events';
import {
  getIsEmailChannel,
  getResourceDocumentLabel,
  isBlogUrlsChannel,
  isEmailCsvResourceDocument,
} from 'src/data/performance';
import { CHANNEL, getChannelPerformanceLabel } from 'src/data/channel';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { ButtonText } from 'src/components/ButtonText';
import { Loaders } from './components/Loaders';
import { useStyles } from './useStyles';

const getChannelLabel = (channel) =>
  getChannelPerformanceLabel(channel) || lowerCase(getResourceDocumentLabel(channel));

const getLabel = ({ progressPercent, channel, isTalkingPointsView }) => {
  if (progressPercent < 20) {
    return isEmailCsvResourceDocument(channel)
      ? 'Uploading...'
      : `Connecting ${getChannelLabel(channel)} with Anyword...`;
  }
  if (progressPercent < 40) {
    return 'Downloading data';
  }
  if (progressPercent < 60) {
    return isTalkingPointsView ? 'Scoring talking points' : 'Labeling copy';
  }
  return isTalkingPointsView ? 'Scoring talking points' : 'Setting up account';
};

const getTrainingModelLabel = ({ progressPercent, channel }) => {
  if (progressPercent < 20) {
    return isEmailCsvResourceDocument(channel)
      ? 'Uploading...'
      : `Connecting ${getChannelLabel(channel)} with Anyword...`;
  }
  if (progressPercent < 40) {
    return 'Downloading data';
  }
  if (progressPercent < 60) {
    return 'Labeling copy';
  }
  if (progressPercent < 80) {
    return 'Setting up account';
  }
  return 'Training your model';
};

const getTitle = (isTrainingModel, alignedAccount) => {
  if (isTrainingModel) {
    return `Train a custom model on ${
      isBlogUrlsChannel(alignedAccount.dataSource)
        ? lowerCase(getChannelLabel(alignedAccount.dataSource))
        : getChannelLabel(alignedAccount.channel || alignedAccount.dataSource)
    } ${
      alignedAccount.channel === CHANNEL.instagram ||
      isEmailCsvResourceDocument(alignedAccount.channel) ||
      isBlogUrlsChannel(alignedAccount.dataSource)
        ? ''
        : 'account'
    }`;
  } else if (getIsEmailChannel(alignedAccount.channel)) {
    return `Connecting email to Anyword: ${alignedAccount.name}`;
  } else {
    return `Connecting account: ${alignedAccount.name}`;
  }
};

export const AccountExtractionLoaderPopup = ({
  open,
  account,
  customModelAccount,
  onClose,
  isTrainingModel,
  isTalkingPointsView,
}) => {
  const styles = useStyles();

  const brandVoicesActions = useBrandVoicesActions();
  const trackEvent = useTrackingEvent();

  const alignedAccount = account || customModelAccount;

  const { runningCustomModels } = useCustomModels();
  const { avgCompletionPercent, errorMessage } = usePerformanceChannelData(
    alignedAccount?.channel,
    alignedAccount?.id
  );

  const runningCustomModel = runningCustomModels.find(
    (cm) => (cm.accountId || cm.id) === customModelAccount?.id
  );

  useEffect(() => {
    if (open) {
      if (
        (avgCompletionPercent === 100 && !customModelAccount) ||
        errorMessage ||
        (customModelAccount && !runningCustomModel)
      ) {
        onClose();
      }
    }
  }, [
    avgCompletionPercent,
    errorMessage,
    brandVoicesActions,
    onClose,
    open,
    customModelAccount,
    runningCustomModel,
  ]);

  const getLabelFunc = isTrainingModel ? getTrainingModelLabel : getLabel;

  const progressPercent = runningCustomModel ? 80 : avgCompletionPercent;

  const handleCloseClick = useCallback(() => {
    brandVoicesActions.queryBrandVoicesAssets();
    onClose();
    trackEvent(events.CIP.clickedDoNotWantToWait, {
      page: events.EVENT_PAGE.editor,
    });
  }, [brandVoicesActions, onClose, trackEvent]);

  if (!alignedAccount) {
    return null;
  }

  return (
    <Dialog
      classes={{ paper: styles.root }}
      open={open}
      fullWidth
      data-testid="AccountExtractionLoaderPopup"
    >
      <DialogTitle>
        <CustomTypography variant={TYPOGRAPHY_VARIANT.semiBold16}>
          {getTitle(isTrainingModel, alignedAccount)}
        </CustomTypography>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <CustomTypography variant={TYPOGRAPHY_VARIANT.medium16}>
          {getLabelFunc({
            progressPercent,
            channel: alignedAccount.channel,
            isTalkingPointsView,
          })}
        </CustomTypography>
        <Loaders
          className={styles.loaders}
          avgCompletionPercent={progressPercent}
          isTrainingModel={isTrainingModel}
          onlyTrainingModel={!!customModelAccount}
        />
        {!isBlogUrlsChannel(alignedAccount.dataSource) && (
          <ButtonText
            className={styles.buttonClose}
            onClick={handleCloseClick}
          >
            Never mind, I don't want to wait
          </ButtonText>
        )}
      </DialogContent>
    </Dialog>
  );
};
